import React from 'react';
import { Platform, StyleSheet, View, ViewStyle } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

type GlowTone = 'teal' | 'orange' | 'blue';

interface Props {
  tone?: GlowTone;
  size?: number;
  intensity?: number;
  style?: ViewStyle | ViewStyle[];
}

const TONES: Record<GlowTone, [string, string, string]> = {
  teal: ['38,208,206', '98,246,255', '21,108,126'],
  orange: ['242,125,38', '255,176,105', '123,64,32'],
  blue: ['67,94,235', '125,155,255', '31,50,130'],
};

const AmbientGlow: React.FC<Props> = ({
  tone = 'teal',
  size = 320,
  intensity = 1,
  style,
}) => {
  const [core, highlight, depth] = TONES[tone];
  const platformBoost = Platform.OS === 'ios' ? 1.1 : 1;
  const outerAlpha = 0.035 * intensity * platformBoost;
  const midAlpha = 0.065 * intensity * platformBoost;
  const coreAlpha = 0.075 * intensity * platformBoost;
  const blurAmount = Math.max(32, size * 0.14);

  return (
    <View
      pointerEvents="none"
      style={[
        styles.root,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          shadowColor: `rgb(${core})`,
          shadowOpacity: Platform.OS === 'ios' ? 0.3 * intensity : 0,
          shadowRadius: size * 0.3,
          filter: [{ blur: blurAmount }],
        },
        style,
      ]}
    >
      <LinearGradient
        colors={[
          `rgba(${highlight},${outerAlpha})`,
          `rgba(${core},${midAlpha})`,
          `rgba(${depth},${outerAlpha})`,
          `rgba(${core},0)`,
        ]}
        locations={[0, 0.36, 0.62, 1]}
        start={{ x: 0.18, y: 0.08 }}
        end={{ x: 0.86, y: 0.94 }}
        style={styles.fill}
      />
      {Platform.OS === 'android' ? (
        <>
          <View
            style={[
              styles.haze,
              {
                backgroundColor: `rgba(${core},${midAlpha})`,
                borderRadius: size * 0.36,
                left: size * 0.14,
                top: size * 0.16,
                width: size * 0.72,
                height: size * 0.72,
              },
            ]}
          />
          <View
            style={[
              styles.haze,
              {
                backgroundColor: `rgba(${highlight},${coreAlpha})`,
                borderRadius: size * 0.24,
                left: size * 0.28,
                top: size * 0.24,
                width: size * 0.48,
                height: size * 0.48,
              },
            ]}
          />
        </>
      ) : (
        <View
          style={[
            styles.iosHaze,
            {
              backgroundColor: `rgba(${highlight},${0.052 * intensity})`,
              borderRadius: size * 0.5,
              left: size * 0.2,
              top: size * 0.2,
              width: size * 0.6,
              height: size * 0.6,
            },
          ]}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    position: 'absolute',
    opacity: Platform.OS === 'android' ? 0.82 : 1,
  },
  fill: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: 999,
  },
  haze: {
    position: 'absolute',
  },
  iosHaze: {
    position: 'absolute',
  },
});

export default AmbientGlow;
